import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  GOOGLE_MAPS_API_KEY,
  createGoogleSession,
  googleKeyTag,
  usableCachedSession,
  type GoogleSession,
} from './googleTiles';

const STORAGE_KEY = 'forest.googleTilesSession';

function readCached(keyTag: string, language: string): GoogleSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? usableCachedSession(JSON.parse(raw), keyTag, language, Date.now()) : null;
  } catch {
    return null;
  }
}

function writeCached(session: GoogleSession, keyTag: string, language: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...session, keyTag, language }));
  } catch {
    // ignore
  }
}

/**
 * A Map Tiles API session for the satellite layer, reused across reloads
 * until it nears expiry. Only requested while the layer is shown.
 */
export function useGoogleTilesSession(enabled: boolean): GoogleSession | null {
  const { i18n } = useTranslation();
  const key = GOOGLE_MAPS_API_KEY;
  const language = i18n.language;
  const { data } = useQuery({
    queryKey: ['google-tiles-session', key ? googleKeyTag(key) : null, language],
    queryFn: async ({ signal }) => {
      const keyTag = googleKeyTag(key!);
      const cached = readCached(keyTag, language);
      if (cached) return cached;
      try {
        const fresh = await createGoogleSession(key!, language, signal);
        writeCached(fresh, keyTag, language);
        return fresh;
      } catch (err) {
        console.warn('[map]', (err as Error).message);
        throw err;
      }
    },
    enabled: enabled && !!key,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
    refetchOnWindowFocus: false,
  });
  return data ?? null;
}
